import type { MapPoint } from './types';

/**
 * The **committed shape**'s undo history — an **immutable snapshot stack**, where undo is `pop()`
 * and never an inverse command (ADR-0018). A snapshot is the shape exactly as it stood immediately
 * before a committed change, so restoring one restores everything about it at once. That is what
 * makes reversing a **Shape mode** switch (ticket 11) put back the **Point**s, their numbering, their
 * connections, the panel layout and the active tool in a single operation rather than five remembered
 * ones — and it is why nothing here knows what changed, only what it looked like before.
 *
 * Generic over the snapshot for the same reason: today the committed shape is the **Point** array,
 * and when circle **Shape mode** lands (tickets 10/11) it widens to `{ tool, points, circle }`. One
 * stack rather than one per axis is what lets a single Undo step back through **Point** actions and
 * mode switches in the order they actually happened, whichever axis each touched.
 *
 * No `origin` (imported Fields and their larger allowance are 113608/113610), and **no redo** —
 * nothing asks for one.
 *
 * Pure, and in `src/lib/maps/` so it runs in the fast `node` test project.
 */

/**
 * Pushes `snapshot` onto the history. A pure append, **not** a defensive copy: nothing in this
 * codebase mutates a committed shape in place — every commit replaces it wholesale — so keeping the
 * same reference here indefinitely is safe, and it is what makes an undone shape the very array that
 * was marked.
 */
export function pushSnapshot<T>(stack: readonly T[], snapshot: T): T[] {
  return [...stack, snapshot];
}

/**
 * Pops the most recent snapshot off the history — 113600: "Undo reverses the most recent action."
 * Returns both halves together so a caller cannot restore a snapshot while forgetting to shorten the
 * stack it came from. `null` once the stack is empty, which is the **level 0** the control's own
 * disabled state reads ("disabled ... once everything has been undone").
 */
export function popSnapshot<T>(stack: readonly T[]): { snapshot: T; rest: T[] } | null {
  if (stack.length === 0) return null;
  return { snapshot: stack[stack.length - 1], rest: stack.slice(0, -1) };
}

/**
 * Translate a shape rigidly: every **Point** moves by `to − from`, in the geographic plane.
 *
 * The whole of "the shape moves as a whole and never deforms" (113609 user stories 32/34/35). It is
 * the acceptance criterion's own arithmetic with the offset cancelled out — `offset = pointer −
 * anchor` then `anchor = point − offset` is `anchor += point − pointer₀`, and every other Point
 * takes the same delta.
 *
 * **There is no scale factor here or anywhere downstream of it**, which is why an **Imported
 * shape** cannot be resized: there is nothing to resize it with, and no handle is rendered to try.
 * And because the result is lat/lng, panning and zooming leave it on the same ground — the map
 * re-projects it, nothing recomputes it.
 *
 * Callers translate from the ORIGINAL committed points on every move, never cumulatively from the
 * previous frame, so rounding cannot accumulate across a long drag.
 *
 * Pure, and in `src/lib/maps/` so it runs in the fast `node` test project.
 */
export function movePoints(points: readonly MapPoint[], from: MapPoint, to: MapPoint): MapPoint[] {
  const dLat = to.lat - from.lat;
  const dLon = to.lon - from.lon;
  return points.map((point) => ({ lat: point.lat + dLat, lon: point.lon + dLon }));
}
